<template>
  <div class="wishes-page">
    <div class="wishes-banner">
      <div class="wishes-stars-wrap" ref="wishesStarsWrap">
        <div class="wishes-stars-css" aria-hidden="true"></div>
        <canvas ref="wishesStarsCanvas" class="wishes-stars-canvas"></canvas>
      </div>
      <div class="wishes-banner-content">
        <div class="wishes-hero">
          <h1 class="wishes-title">Wishes!</h1>
          <p class="wishes-subtitle">Make a wish!</p>
        </div>
        <div class="wishes-scope" role="group" aria-label="Wish list scope">
          <button
            type="button"
            class="scope-btn"
            :class="{ active: wishesScope === 'mine' }"
            @click="wishesScope = 'mine'"
          >
            My wishes
          </button>
          <button
            type="button"
            class="scope-btn"
            :class="{ active: wishesScope === 'all' }"
            @click="wishesScope = 'all'"
          >
            All wishes
          </button>
        </div>
      </div>
    </div>
    <p v-if="loading" class="loading-message">Loading wishes…</p>
    <template v-else>
      <p class="count-msg">
        {{ wishesOpenActiveSorted.length }} open,
        {{ wishesPausedSorted.length }} paused,
        {{ wishesCompletedSorted.length }} completed
        ({{ wishesInScope.length }} in view{{ wishesScope === 'mine' ? ' · yours only' : '' }}{{ wishesScope === 'all' ? ` · ${wishes.length} total` : '' }})
      </p>

      <div class="wishes-section-block">
        <h2 class="wishes-section-title">Open wishes</h2>
        <div class="scroll-box">
          <table class="table wishes-table">
            <colgroup>
              <col class="col-due">
              <col class="col-customer">
              <col class="col-org">
              <col class="col-wish">
              <col class="col-priority">
              <col class="col-status">
              <col class="col-assigned">
            </colgroup>
            <thead>
              <tr>
                <th scope="col" class="wishes-th-num">Due</th>
                <th scope="col" class="wishes-th-customer">Customer</th>
                <th scope="col" class="wishes-th-org">Customer main organization</th>
                <th
                  scope="col"
                  class="wishes-th-wish"
                  title="Click a wish in the list to open details"
                >Wish</th>
                <th scope="col" class="wishes-th-narrow">Priority</th>
                <th scope="col" class="wishes-th-narrow">Status</th>
                <th scope="col" class="wishes-th-assign">Assigned / also</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="w in wishesOpenActiveSorted" :key="w.id">
                <td>{{ formatDueDate(w.due_date) }}</td>
                <td>
                  <router-link
                    v-if="w.customer"
                    :to="`/customers/${w.customer.id}`"
                    class="customer-link truncate"
                    :title="customerName(w.customer)"
                  >
                    {{ customerName(w.customer) }}
                  </router-link>
                  <span v-else>—</span>
                </td>
                <td><span class="truncate" :title="w.customer?.company || '—'">{{ w.customer?.company || '—' }}</span></td>
                <td>
                  <button
                    type="button"
                    class="wish-text-btn truncate"
                    :title="wishRowTooltip(w)"
                    @click="openDetail(w)"
                  >
                    {{ oneLineWish(w) }}
                  </button>
                </td>
                <td>
                  <select
                    :value="wishPriorityApiValue(w)"
                    class="wish-priority-select"
                    @change="updateWishPriority(w, $event.target.value)"
                  >
                    <option value="high">{{ priorityOptionLabel('high') }}</option>
                    <option value="med">{{ priorityOptionLabel('med') }}</option>
                    <option value="low">{{ priorityOptionLabel('low') }}</option>
                  </select>
                </td>
                <td>
                  <select
                    :value="wishStatusDisplayValue(w)"
                    class="wish-status-select"
                    @change="updateWishStatus(w, $event.target.value)"
                  >
                    <option value="new">new</option>
                    <option value="in_progress">in progress</option>
                    <option value="paused">paused</option>
                    <option value="completed">completed</option>
                  </select>
                </td>
                <td>
                  <select
                    :value="w.assigned_to || ''"
                    class="wish-assign-select"
                    @change="updateWishAssigned(w, $event.target.value)"
                  >
                    <option value="">—</option>
                    <option v-for="name in chibiTeamNames" :key="name" :value="name">{{ name }}</option>
                  </select>
                  <WishAlsoTagged
                    :tagged="wishTaggedList(w)"
                    :assigned-to="w.assigned_to || ''"
                    :team-names="chibiTeamNames"
                    @add="addWishTag(w, $event)"
                    @remove="removeWishTag(w, $event)"
                  />
                </td>
              </tr>
              <tr v-if="!wishesOpenActiveSorted.length">
                <td colspan="7" class="empty-row">No open wishes.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="wishes-archive">
        <div class="wishes-archive-tabs" role="tablist">
          <button
            type="button"
            class="wishes-archive-tab tab-completed"
            :class="{ active: archiveTab === 'completed' }"
            @click="archiveTab = 'completed'"
          >
            Completed <span class="wishes-archive-count">{{ wishesCompletedSorted.length }}</span>
          </button>
          <button
            type="button"
            class="wishes-archive-tab tab-paused"
            :class="{ active: archiveTab === 'paused' }"
            @click="archiveTab = 'paused'"
          >
            Paused <span class="wishes-archive-count">{{ wishesPausedSorted.length }}</span>
          </button>
        </div>
        <div class="scroll-box wishes-archive-panel">
          <table class="table wishes-table">
            <colgroup>
              <col class="col-due">
              <col class="col-customer">
              <col class="col-org">
              <col class="col-wish">
              <col class="col-priority">
              <col class="col-status">
              <col class="col-assigned">
            </colgroup>
            <thead>
              <tr>
                <th scope="col" class="wishes-th-num">Due</th>
                <th scope="col" class="wishes-th-customer">Customer</th>
                <th scope="col" class="wishes-th-org">Customer main organization</th>
                <th scope="col" class="wishes-th-wish">Wish</th>
                <th scope="col" class="wishes-th-narrow">Priority</th>
                <th scope="col" class="wishes-th-narrow">Status</th>
                <th scope="col" class="wishes-th-assign">Assigned / also</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="w in archiveRows" :key="w.id">
                <td>{{ formatDueDate(w.due_date) }}</td>
                <td>
                  <router-link
                    v-if="w.customer"
                    :to="`/customers/${w.customer.id}`"
                    class="customer-link truncate"
                    :title="customerName(w.customer)"
                  >
                    {{ customerName(w.customer) }}
                  </router-link>
                  <span v-else>—</span>
                </td>
                <td><span class="truncate" :title="w.customer?.company || '—'">{{ w.customer?.company || '—' }}</span></td>
                <td>
                  <button
                    type="button"
                    class="wish-text-btn truncate"
                    :title="wishRowTooltip(w)"
                    @click="openDetail(w)"
                  >
                    {{ oneLineWish(w) }}
                  </button>
                </td>
                <td>
                  <select
                    :value="wishPriorityApiValue(w)"
                    class="wish-priority-select"
                    @change="updateWishPriority(w, $event.target.value)"
                  >
                    <option value="high">{{ priorityOptionLabel('high') }}</option>
                    <option value="med">{{ priorityOptionLabel('med') }}</option>
                    <option value="low">{{ priorityOptionLabel('low') }}</option>
                  </select>
                </td>
                <td>
                  <select
                    :value="wishStatusDisplayValue(w)"
                    class="wish-status-select"
                    @change="updateWishStatus(w, $event.target.value)"
                  >
                    <option value="new">new</option>
                    <option value="in_progress">in progress</option>
                    <option value="paused">paused</option>
                    <option value="completed">completed</option>
                  </select>
                </td>
                <td>
                  <select
                    :value="w.assigned_to || ''"
                    class="wish-assign-select"
                    @change="updateWishAssigned(w, $event.target.value)"
                  >
                    <option value="">—</option>
                    <option v-for="name in chibiTeamNames" :key="name" :value="name">{{ name }}</option>
                  </select>
                  <WishAlsoTagged
                    :tagged="wishTaggedList(w)"
                    :assigned-to="w.assigned_to || ''"
                    :team-names="chibiTeamNames"
                    @add="addWishTag(w, $event)"
                    @remove="removeWishTag(w, $event)"
                  />
                </td>
              </tr>
              <tr v-if="!archiveRows.length">
                <td colspan="7" class="empty-row">
                  {{ archiveTab === 'paused' ? 'No paused wishes.' : 'No completed wishes yet.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- Wish detail modal -->
    <div v-if="detailWish" class="modal-backdrop wishes-modal-backdrop" @click.self="closeDetail">
      <div class="modal-card wishes-detail-card">
        <div class="wishes-detail-header">
          <h2 class="wishes-detail-title">{{ detailWish.title || 'Untitled' }}</h2>
          <button type="button" class="wishes-detail-close" @click="closeDetail" aria-label="Close">✕</button>
        </div>
        <div class="wishes-detail-body">
          <p v-if="detailWish.customer" class="wishes-detail-customer">
            <router-link :to="`/customers/${detailWish.customer.id}`" class="customer-link">
              {{ customerName(detailWish.customer) }}
            </router-link>
            <span v-if="detailWish.customer.company"> · {{ detailWish.customer.company }}</span>
          </p>
          <p class="wishes-detail-meta">
            {{ wishPriorityPretty(detailWish) }} · {{ statusLabel(detailWish.status) }} · Due {{ formatDueDate(detailWish.due_date) }}
            <span v-if="detailWish.assigned_to"> · {{ detailWish.assigned_to }}</span>
            <span v-if="detailWish.author_email"> · by {{ detailWish.author_email }}</span>
          </p>
          <WishAlsoTagged
            v-if="detailWish.customer"
            :tagged="wishTaggedList(detailWish)"
            :assigned-to="detailWish.assigned_to || ''"
            :team-names="chibiTeamNames"
            @add="addWishTag(detailWish, $event)"
            @remove="removeWishTag(detailWish, $event)"
          />
          <div v-if="detailWish.body" class="wishes-detail-description" v-html="detailWish.body"></div>
          <p v-else class="wishes-detail-empty">No description.</p>
        </div>
      </div>
    </div>

    <div v-if="showFulfillCelebration" class="fulfill-celebration">
      <div class="fulfill-celebration-glow"></div>
      <img src="/img/you_re_did_it.png" alt="You're did it!" class="celebrate-star" />
      <p class="celebrate-text">You're did it!!!</p>
    </div>
  </div>
</template>

<script>
import confetti from 'canvas-confetti'
import WishesService from '../services/WishesService'
import chibiTeamMixin from '@/mixins/chibiTeamMixin'
import WishAlsoTagged from '@/components/WishAlsoTagged.vue'

const WISH_STATUS_OPTIONS = [
  { value: 'new', label: 'new' },
  { value: 'in_progress', label: 'in progress' },
  { value: 'paused', label: 'paused' },
  { value: 'completed', label: 'completed' }
]

export default {
  name: 'WishesPage',
  mixins: [chibiTeamMixin],
  components: { WishAlsoTagged },
  props: ['username'],
  data() {
    return {
      wishes: [],
      loading: true,
      detailWish: null,
      showFulfillCelebration: false,
      wishesScope: 'mine',
      archiveTab: 'completed',
      wishesStarsRafId: null,
      wishesStarsCtx: null
    }
  },
  computed: {
    viewerEmailNorm() {
      return (this.username || '').toString().toLowerCase().trim()
    },
    wishesInScope() {
      const all = this.wishes || []
      if (this.wishesScope !== 'mine') return all
      return all.filter(w => this.wishIsMine(w))
    },
    wishesOpenSorted() {
      const open = this.wishesInScope.filter(w => !this.isWishCompleted(w))
      return open.slice().sort((a, b) => {
        const pr = this.priorityRank(a) - this.priorityRank(b)
        if (pr !== 0) return pr
        return this.compareDueDateAsc(a.due_date, b.due_date)
      })
    },
    wishesOpenActiveSorted() {
      return this.wishesOpenSorted.filter(w => this.normalizeStatus(w.status) !== 'paused')
    },
    wishesPausedSorted() {
      return this.wishesOpenSorted.filter(w => this.normalizeStatus(w.status) === 'paused')
    },
    wishesCompletedSorted() {
      const done = this.wishesInScope.filter(w => this.isWishCompleted(w))
      return done.slice().sort((a, b) => {
        const fb = this.fulfilledTimeMs(b.fulfilled_at)
        const fa = this.fulfilledTimeMs(a.fulfilled_at)
        if (fb !== fa) return fb - fa
        return this.dueTimeMs(b.due_date) - this.dueTimeMs(a.due_date)
      })
    },
    archiveRows() {
      return this.archiveTab === 'paused' ? this.wishesPausedSorted : this.wishesCompletedSorted
    }
  },
  created() {
    this.load()
  },
  mounted() {
    this.$nextTick(() => this.startWishesStarsCanvas())
  },
  beforeUnmount() {
    this.stopWishesStarsCanvas()
  },
  methods: {
    startWishesStarsCanvas() {
      const wrap = this.$refs.wishesStarsWrap
      const canvas = this.$refs.wishesStarsCanvas
      if (!wrap || !canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      let width = 0
      let height = 0
      let stars = []
      let meteors = []
      const starCount = 200
      const meteorRate = 10
      const maxMeteors = 40

      const resize = () => {
        width = wrap.clientWidth
        height = wrap.clientHeight
        if (width <= 0 || height <= 0) return
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        canvas.width = width * dpr
        canvas.height = height * dpr
        canvas.style.width = width + 'px'
        canvas.style.height = height + 'px'
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        stars = []
        for (let i = 0; i < starCount; i++) {
          stars.push({
            x: Math.random() * width,
            y: Math.random() * height,
            r: Math.random() * 1.8 + 0.5,
            a: Math.random() * 0.5 + 0.4,
            tw: Math.random() * 0.02 + 0.005
          })
        }
      }

      const spawnMeteor = () => {
        const roll = Math.random()
        let startX = -Math.random() * width * 0.4
        let startY = Math.random() * height * 0.7
        if (roll > 0.6) {
          startX = width * (0.1 + Math.random() * 0.25)
          startY = Math.random() * height * 0.55
        } else if (roll > 0.3) {
          startX = width * (0.4 + Math.random() * 0.3)
          startY = Math.random() * height * 0.55
        } else if (roll > 0.1) {
          startX = width * (0.65 + Math.random() * 0.25)
          startY = Math.random() * height * 0.5
        }
        let angle
        const ar = Math.random()
        if (ar < 0.25) angle = Math.PI * (0.1 + Math.random() * 0.06)
        else if (ar < 0.5) angle = Math.PI * (0.22 + Math.random() * 0.08)
        else if (ar < 0.75) angle = Math.PI * (0.32 + Math.random() * 0.08)
        else angle = -Math.PI * (0.12 + Math.random() * 0.08)
        const speed = Math.random() * 4 + 7
        const length = Math.random() * 220 + 280
        const widthPx = Math.random() * 4 + 5
        meteors.push({
          x: startX,
          y: startY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          len: length,
          w: widthPx,
          life: 0,
          maxLife: Math.random() * 50 + 70
        })
      }

      let lastTime = performance.now()
      let meteorAccumulator = 0

      const render = (now) => {
        if (!this.wishesStarsCtx) return
        const dt = Math.min((now - lastTime) / 1000, 0.033)
        lastTime = now
        meteorAccumulator += dt
        while (meteorAccumulator > 1 / meteorRate) {
          meteorAccumulator -= 1 / meteorRate
          if (meteors.length < maxMeteors) spawnMeteor()
        }
        ctx.clearRect(0, 0, width, height)
        ctx.fillStyle = 'rgba(255,255,255,0.92)'
        stars.forEach(s => {
          s.a += s.tw * (Math.random() > 0.5 ? 1 : -1)
          s.a = Math.max(0.2, Math.min(1, s.a))
          ctx.globalAlpha = s.a
          ctx.beginPath()
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
          ctx.fill()
        })
        ctx.globalAlpha = 1
        meteors = meteors.filter(m => m.life < m.maxLife && m.x < width * 1.8 && m.y < height * 1.8)
        meteors.forEach(m => {
          m.x += m.vx
          m.y += m.vy
          m.life += 1
          const tailFactor = 14
          const tailX = m.x - m.vx * (m.len / tailFactor)
          const tailY = m.y - m.vy * (m.len / tailFactor)
          const grad = ctx.createLinearGradient(m.x, m.y, tailX, tailY)
          grad.addColorStop(0, 'rgba(255,255,255,1)')
          grad.addColorStop(0.15, 'rgba(255,255,255,0.95)')
          grad.addColorStop(0.4, 'rgba(255,255,255,0.75)')
          grad.addColorStop(0.7, 'rgba(255,248,240,0.35)')
          grad.addColorStop(1, 'rgba(255,255,255,0)')
          ctx.strokeStyle = grad
          ctx.lineWidth = m.w
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'
          ctx.beginPath()
          ctx.moveTo(m.x, m.y)
          ctx.lineTo(tailX, tailY)
          ctx.stroke()
        })
        this.wishesStarsRafId = requestAnimationFrame(render)
      }

      resize()
      this.wishesStarsCtx = ctx
      const ro = new ResizeObserver(() => resize())
      ro.observe(wrap)
      this._wishesStarsResizeObserver = ro
      this.wishesStarsRafId = requestAnimationFrame(render)
    },
    stopWishesStarsCanvas() {
      if (this.wishesStarsRafId != null) {
        cancelAnimationFrame(this.wishesStarsRafId)
        this.wishesStarsRafId = null
      }
      this.wishesStarsCtx = null
      if (this._wishesStarsResizeObserver) {
        this._wishesStarsResizeObserver.disconnect()
        this._wishesStarsResizeObserver = null
      }
    },
    /** API uses high / med / low; UI historically used medium. */
    wishPriorityApiValue(w) {
      const p = (w && w.priority) ? String(w.priority).toLowerCase() : 'med'
      if (p === 'high') return 'high'
      if (p === 'low') return 'low'
      return 'med'
    },
    priorityRank(w) {
      const p = this.wishPriorityApiValue(w)
      if (p === 'high') return 0
      if (p === 'med') return 1
      return 2
    },
    priorityOptionLabel(apiVal) {
      const labels = {
        high: '🚨 High',
        med: '✨ Medium',
        low: '🌱 Low'
      }
      return labels[apiVal] || apiVal
    },
    wishPriorityPretty(w) {
      return this.priorityOptionLabel(this.wishPriorityApiValue(w))
    },
    async load() {
      this.loading = true
      try {
        const { data } = await WishesService.listAll()
        this.wishes = (data.wishes || []).slice()
      } catch (err) {
        console.error('Load wishes failed', err)
        this.wishes = []
      } finally {
        this.loading = false
      }
    },
    customerName(c) {
      if (!c) return '—'
      const name = [c.first_name, c.last_name].filter(Boolean).join(' ')
      return name || '—'
    },
    formatDueDate(d) {
      if (!d) return '—'
      const date = typeof d === 'string' ? d : (d && d.toISOString?.().slice(0, 10))
      if (!date) return '—'
      try {
        const [y, m, day] = date.slice(0, 10).split('-')
        return `${m}/${day}/${y?.slice(2) || ''}`
      } catch {
        return date
      }
    },
    stripHtml(html) {
      if (!html) return ''
      const div = document.createElement('div')
      div.innerHTML = html
      return (div.textContent || div.innerText || '').trim()
    },
    /** Table cell: title first (like customer page); body is for the modal. */
    oneLineWish(w) {
      const raw = ((w.title && String(w.title).trim()) || this.stripHtml(w.body || '').trim() || 'Untitled')
      const text = raw.replace(/\s+/g, ' ')
      return text.slice(0, 80) + (text.length > 80 ? '…' : '')
    },
    wishRowTooltip(w) {
      const title = (w.title && String(w.title).trim()) || ''
      const bodyPlain = this.stripHtml(w.body || '').trim()
      if (title && bodyPlain && bodyPlain !== title) return `${title} — ${bodyPlain.slice(0, 200)}${bodyPlain.length > 200 ? '…' : ''}`
      return title || bodyPlain || 'Untitled'
    },
    dueTimeMs(d) {
      if (!d) return 0
      const t = new Date(d).getTime()
      return Number.isNaN(t) ? 0 : t
    },
    compareDueDateAsc(da, db) {
      const wa = this.dueTimeMsForSort(da)
      const wb = this.dueTimeMsForSort(db)
      return wa - wb
    },
    /** Null / missing due dates sort last (match API NULLS LAST). */
    dueTimeMsForSort(d) {
      if (!d) return Number.POSITIVE_INFINITY
      const t = new Date(d).getTime()
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
    },
    fulfilledTimeMs(f) {
      if (f == null || f === '') return 0
      const t = new Date(f).getTime()
      return Number.isNaN(t) ? 0 : t
    },
    isWishCompleted(w) {
      if (w.fulfilled_at != null && w.fulfilled_at !== '') return true
      return this.normalizeStatus(w.status) === 'completed'
    },
    normalizeStatus(s) {
      const map = { open: 'new', in_progress: 'in_progress', paused: 'paused', completed: 'completed', done: 'completed', fulfilled: 'completed' }
      return map[s] || 'new'
    },
    /** For dropdown: fulfilled wishes always show "completed". */
    wishStatusDisplayValue(w) {
      if (w.fulfilled_at) return 'completed'
      return this.normalizeStatus(w.status)
    },
    wishAlreadyFulfilledForCelebration(w) {
      if (w.fulfilled_at != null && w.fulfilled_at !== '') return true
      return this.normalizeStatus(w.status) === 'completed'
    },
    runFulfillCelebration() {
      this.showFulfillCelebration = true
      try {
        confetti({ particleCount: 120, spread: 70, origin: { y: 0.7 } })
      } catch (_) { /* confetti unavailable */ }
      setTimeout(() => { this.showFulfillCelebration = false }, 3500)
    },
    statusLabel(val) {
      const o = WISH_STATUS_OPTIONS.find(opt => opt.value === (this.normalizeStatus(val) || val))
      return o ? o.label : (val || '—')
    },
    openDetail(w) {
      this.detailWish = w
    },
    closeDetail() {
      this.detailWish = null
    },
    async updateWishStatus(w, value) {
      if (!w.customer?.id) return
      const alreadyFulfilled = this.wishAlreadyFulfilledForCelebration(w)
      try {
        const { data } = await WishesService.update(w.customer.id, w.id, { status: value })
        w.status = data.status
        if ('fulfilled_at' in data) w.fulfilled_at = data.fulfilled_at
        if ('due_date' in data && data.due_date != null) w.due_date = data.due_date
        if (!alreadyFulfilled && value === 'completed') {
          this.runFulfillCelebration()
        }
      } catch (err) {
        console.error('Update wish status failed', err)
        const msg = err.response?.data?.errors?.join?.('; ') || err.message || 'Could not update status.'
        alert(msg)
      }
    },
    async updateWishAssigned(w, value) {
      if (!w.customer?.id) return
      const assigned_to = value.trim() || null
      const tagged = this.wishTaggedList(w).filter((n) => n !== assigned_to)
      try {
        const { data } = await WishesService.update(w.customer.id, w.id, {
          assigned_to,
          tagged_members: tagged
        })
        w.assigned_to = data.assigned_to
        w.tagged_members = data.tagged_members || tagged
        if (this.detailWish && this.detailWish.id === w.id) {
          this.detailWish.assigned_to = w.assigned_to
          this.detailWish.tagged_members = w.tagged_members
        }
      } catch (err) {
        console.error('Update wish assigned_to failed', err)
      }
    },
    async setWishTags(w, tagged) {
      if (!w.customer?.id) return
      const next = tagged.filter((n) => n && n !== (w.assigned_to || '').trim())
      try {
        const { data } = await WishesService.update(w.customer.id, w.id, { tagged_members: next })
        w.tagged_members = data.tagged_members || next
        if (this.detailWish && this.detailWish.id === w.id) {
          this.detailWish.tagged_members = w.tagged_members
        }
      } catch (err) {
        console.error('Update wish tagged_members failed', err)
      }
    },
    addWishTag(w, name) {
      const current = this.wishTaggedList(w)
      if (current.includes(name)) return
      this.setWishTags(w, [...current, name])
    },
    removeWishTag(w, name) {
      this.setWishTags(w, this.wishTaggedList(w).filter((n) => n !== name))
    },
    async updateWishPriority(w, value) {
      if (!w.customer?.id) return
      try {
        const { data } = await WishesService.update(w.customer.id, w.id, { priority: value })
        if (data && data.priority != null) w.priority = data.priority
      } catch (err) {
        console.error('Update wish priority failed', err)
        alert(err.response?.data?.errors?.join?.('; ') || 'Could not update priority.')
      }
    }
  }
}
</script>

<style scoped>
.wishes-page { padding: 0 .5rem 1.5rem }
.wishes-banner {
  position: relative;
  margin: 0 -0.5rem 1rem;
  padding: 1.75rem 1rem 1.35rem;
  min-height: 200px;
  overflow: hidden;
  border-radius: 0 0 14px 14px;
  background: radial-gradient(ellipse at 50% 0%, rgba(30, 58, 90, 0.55), transparent 70%);
}
.wishes-stars-wrap {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}
.wishes-stars-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}
.wishes-stars-css {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1.6px 1.6px at 20px 30px, rgba(255,255,255,0.95) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1.2px 1.2px at 70px 90px, rgba(255,255,255,0.85) 0, rgba(255,255,255,0) 60%),
    radial-gradient(2px 2px at 140px 40px, rgba(255,255,255,0.98) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1.4px 1.4px at 200px 120px, rgba(255,255,255,0.9) 0, rgba(255,255,255,0) 60%),
    radial-gradient(2.2px 2.2px at 260px 70px, rgba(255,255,255,1) 0, rgba(255,255,255,0) 60%);
  background-size: 180px 140px, 220px 180px, 260px 220px, 320px 260px, 380px 300px;
  background-position: 0 0, 40px 60px, 80px 20px, 120px 90px, 160px 40px;
  opacity: 0.85;
  animation: wishesStarDrift 8s linear infinite;
  pointer-events: none;
  z-index: 0;
  filter: drop-shadow(0 0 12px rgba(255,255,255,0.6));
}
.wishes-stars-css::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1px 1px at 30px 20px, rgba(255,255,255,0.8) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1.5px 1.5px at 90px 110px, rgba(255,255,255,0.9) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1px 1px at 170px 60px, rgba(255,255,255,0.75) 0, rgba(255,255,255,0) 60%),
    radial-gradient(2px 2px at 240px 130px, rgba(255,255,255,0.95) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1.2px 1.2px at 310px 80px, rgba(255,255,255,0.85) 0, rgba(255,255,255,0) 60%);
  background-size: 120px 90px, 160px 120px, 200px 160px, 240px 180px, 280px 220px;
  background-position: 10px 20px, 40px 60px, 70px 30px, 100px 80px, 130px 50px;
  opacity: 0.9;
  animation: wishesStarTwinkle 2.2s ease-in-out infinite;
}
.wishes-stars-css::after {
  content: "";
  position: absolute;
  inset: -50% -50%;
  background-image:
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,1) 40%, rgba(255,252,245,0.6) 55%, rgba(255,255,255,0) 75%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.98) 40%, rgba(255,250,240,0.5) 58%, rgba(255,255,255,0) 78%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.95) 42%, rgba(255,248,240,0.45) 56%, rgba(255,255,255,0) 76%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.92) 38%, rgba(255,252,245,0.5) 54%, rgba(255,255,255,0) 74%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.9) 42%, rgba(255,250,235,0.4) 58%, rgba(255,255,255,0) 78%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.88) 40%, rgba(255,248,240,0.35) 56%, rgba(255,255,255,0) 76%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 22%, rgba(255,255,255,0.9) 42%, rgba(255,252,245,0.4) 58%, rgba(255,255,255,0) 78%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 18%, rgba(255,255,255,0.85) 38%, rgba(255,248,235,0.3) 55%, rgba(255,255,255,0) 75%, rgba(255,255,255,0) 100%);
  background-size: 880px 26px, 1020px 24px, 1180px 28px, 920px 22px, 1060px 24px, 1120px 26px, 960px 20px, 1000px 22px;
  background-repeat: no-repeat;
  background-position: -95% 5%, -115% 18%, -125% 32%, -75% 48%, -100% 62%, -118% 78%, -88% 88%, -105% 12%;
  opacity: 1;
  animation: wishesMeteorStorm 2.2s linear infinite;
  filter: drop-shadow(0 0 20px rgba(255,255,255,0.9));
  mix-blend-mode: screen;
}
@keyframes wishesStarDrift {
  0% { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(10%, -6%, 0); }
}
@keyframes wishesStarTwinkle {
  0%, 100% { opacity: 0.75; }
  50% { opacity: 1; }
}
@keyframes wishesMeteorStorm {
  0% { transform: translate3d(-60%, -30%, 0) rotate(0.5deg); opacity: 0.2; }
  15% { opacity: 1; }
  70% { transform: translate3d(120%, 55%, 0) rotate(0.5deg); opacity: 1; }
  100% { transform: translate3d(220%, 90%, 0) rotate(0.5deg); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .wishes-stars-css,
  .wishes-stars-css::before,
  .wishes-stars-css::after {
    animation: none;
  }
}
.wishes-banner-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
.wishes-hero {
  text-align: center;
  padding: 0.35rem 0 0.15rem;
}
.wishes-title {
  margin: 0 0 0.35rem;
  font-size: clamp(1.9rem, 4.5vw, 2.75rem);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.9), 0 0 40px rgba(255, 255, 255, 0.5);
  filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.6));
  animation: wishesTitleGlow 4s ease-in-out infinite;
}
@keyframes wishesTitleGlow {
  0%, 100% { filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.6)); }
  50% { filter: drop-shadow(0 0 24px rgba(255, 255, 255, 0.85)); }
}
.wishes-subtitle {
  margin: 0;
  font-size: 1rem;
  color: rgba(226, 232, 240, 0.85);
  font-weight: 400;
  letter-spacing: 0.04em;
}
.wishes-scope {
  display: inline-flex;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,.22);
  backdrop-filter: blur(6px);
  background: rgba(0, 0, 0, 0.22);
}
.scope-btn {
  background: rgba(255,255,255,.06);
  color: rgba(255,255,255,.85);
  border: none;
  padding: 0.4rem 0.85rem;
  font-size: 0.92rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.scope-btn + .scope-btn { border-left: 1px solid rgba(255,255,255,.15) }
.scope-btn:hover { background: rgba(255,255,255,.12); color: #fff }
.scope-btn.active {
  background: rgba(26, 188, 156, 0.35);
  color: #ecfdf5;
  font-weight: 600;
}
.loading-message { font-size: 1.1rem; font-style: italic; color: #ffa; margin: 1rem 0 }
.count-msg { font-size: 0.95rem; color: rgba(255,255,255,.85); margin-bottom: 0.75rem }
.wishes-section-block { margin-bottom: 1.5rem }
.wishes-section-block:last-child { margin-bottom: 0 }
.wishes-section-title {
  margin: 0 0 0.25rem;
  font-size: 1.15rem;
  font-weight: 600;
  color: #fff;
}
.wishes-section-hint {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  color: rgba(255,255,255,.65);
}
.scroll-box {
  max-height: 42vh;
  overflow: auto;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 4px;
  padding: 4px;
}
.wishes-table { border-collapse: collapse; width: 100%; min-width: 1040px; table-layout: fixed }
.wishes-table .col-due { width: 7.5rem }
.wishes-table .col-customer { width: 11% }
.wishes-table .col-org { width: 14% }
.wishes-table .col-wish { width: auto }
.wishes-table .col-priority { width: 8.5rem }
.wishes-table .col-status { width: 9.5rem }
.wishes-table .col-assigned { width: 8.5rem }
.wish-priority-select {
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.3);
  color: #fff;
  padding: 0.25rem 0.35rem;
  border-radius: 4px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.wishes-table th {
  text-align: left;
  padding: 0.45rem 0.4rem;
  border-bottom: 2px solid #1abc9c;
  color: #fff;
  white-space: normal;
  line-height: 1.25;
  vertical-align: bottom;
  font-weight: 600;
  font-size: 0.82rem;
  hyphens: auto;
  word-wrap: break-word;
}
.wishes-table th.wishes-th-narrow,
.wishes-table th.wishes-th-num { white-space: nowrap }
.wishes-table td { padding: 0.5rem 0.4rem; border-bottom: 1px solid rgba(255,255,255,.12); color: #fff; vertical-align: middle }
.wishes-table td:nth-child(2) .customer-link,
.wishes-table td:nth-child(2) .truncate { max-width: 100%; display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap }
.wishes-table td:nth-child(3) .truncate { max-width: 100%; display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap }
.wishes-table td:nth-child(4) { min-width: 0 }
.customer-link { color: #fbd38d; text-decoration: none }
.customer-link:hover { color: #fff; text-decoration: underline }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block }
/* Link-style control: undo global `button:hover { transform: scale(1.05) }` so rows don’t break */
.wish-text-btn {
  background: none;
  border: none;
  color: #c3dafe;
  cursor: pointer;
  padding: 0;
  margin: 0;
  text-align: left;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  transform: none;
  transition: color 0.18s ease, text-decoration-color 0.18s ease;
}
.wish-text-btn:hover,
.wish-text-btn:focus {
  transform: none;
  background: none;
  background-color: transparent;
  outline: none;
  color: #eef2ff;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.22em;
  text-decoration-color: rgba(199, 210, 254, 0.5);
}
.wish-text-btn:focus-visible {
  outline: 2px solid rgba(165, 180, 252, 0.7);
  outline-offset: 2px;
}
.wish-status-select, .wish-assign-select {
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.3);
  color: #fff;
  padding: 0.25rem 0.35rem;
  border-radius: 4px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.empty-row { color: rgba(255,255,255,.6); font-style: italic; padding: 1.5rem }

/* Modal */
.wishes-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.wishes-detail-card {
  background: #2c3e50;
  border-radius: 8px;
  max-width: 520px;
  width: 90%;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0,0,0,.4);
}
.wishes-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,.15);
}
.wishes-detail-title { margin: 0; font-size: 1.25rem; color: #fff }
.wishes-detail-close {
  background: none;
  border: none;
  color: rgba(255,255,255,.8);
  cursor: pointer;
  font-size: 1.25rem;
  padding: 0 .25rem;
}
.wishes-detail-close:hover { color: #fff }
.wishes-detail-body {
  padding: 1rem 1.25rem;
  overflow-y: auto;
  color: rgba(255,255,255,.9);
}
.wishes-detail-customer { margin: 0 0 0.5rem; font-size: 0.95rem }
.wishes-detail-meta { margin: 0 0 0.75rem; font-size: 0.9rem; color: rgba(255,255,255,.7) }
.wishes-detail-description {
  margin: 0;
  line-height: 1.5;
  word-break: break-word;
}
.wishes-detail-description :deep(p) { margin: 0 0 0.5rem }
.wishes-detail-empty { margin: 0; font-style: italic; color: rgba(255,255,255,.5) }

.wishes-archive {
  margin-top: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.15);
}
.wishes-archive-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
}
.wishes-archive-tab {
  flex: 1;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  padding: 0.65rem 1rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border-bottom: 2px solid transparent;
  margin: 0;
}
.wishes-archive-tab.tab-completed.active {
  color: #6ee7b7;
  border-bottom-color: #1abc9c;
  background: rgba(26, 188, 156, 0.12);
}
.wishes-archive-tab.tab-paused.active {
  color: #fda4af;
  border-bottom-color: #dc143c;
  background: rgba(220, 20, 60, 0.12);
}
.wishes-archive-count {
  font-size: 0.75rem;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
}
.wishes-archive-panel {
  max-height: 45vh;
}

/* Same celebration as customer wish “Mark fulfilled” (above modal at z-index 1000) */
.fulfill-celebration {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse 120% 100% at 50% 50%, rgba(16, 185, 129, 0.15) 0%, rgba(2, 6, 23, 0.92) 70%);
  pointer-events: none;
}
.fulfill-celebration-glow {
  position: absolute;
  inset: -20%;
  background: radial-gradient(circle at 50% 50%, rgba(251, 191, 36, 0.12) 0%, transparent 55%);
  pointer-events: none;
}
.celebrate-star {
  width: min(380px, 85vw);
  height: auto;
  margin-bottom: 1.25rem;
  filter: drop-shadow(0 0 40px rgba(251, 191, 36, 0.6));
  position: relative;
  z-index: 1;
}
.celebrate-text {
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
  background: linear-gradient(135deg, #fef08a 0%, #fbbf24 50%, #f59e0b 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 40px rgba(251, 191, 36, 0.3);
  position: relative;
  z-index: 1;
}
</style>
